import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ArrowLeft } from 'lucide-react';
import { authApi } from '@/api-client';

export function AdminLogin() {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authApi.login(password);
      if (result.success) {
        navigate('/admin', { replace: true });
      } else {
        setError('Falsches Passwort');
      }
    } catch {
      setError('Fehler bei der Anmeldung');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      {/* Hintergrund-Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gold/[0.04] blur-3xl pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-surface rounded-3xl border border-white/[0.06] p-8 shadow-card">
          {/* Icon */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gold-dim border border-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-gold" />
            </div>
            <h1 className="font-display text-2xl font-bold italic text-text-primary mb-2">
              Admin-Bereich
            </h1>
            <p className="text-text-muted text-sm">
              Bitte gib das Admin-Passwort ein.
            </p>
          </div>

          {/* Formular */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:bg-gold/5 focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all"
                placeholder="••••••••"
              />
            </div>

            {/* Fehlermeldung */}
            {error && (
              <div className="bg-crimson-dim border border-crimson/20 text-crimson text-sm px-4 py-2.5 rounded-xl">
                {error}
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading || !password}
              className="w-full py-3.5 bg-gold text-surface-primary font-bold rounded-xl shadow-gold hover:shadow-gold-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-surface-primary border-t-transparent rounded-full animate-spin" />
                  Prüfe...
                </span>
              ) : (
                'Anmelden'
              )}
            </button>
          </form>

          {/* Zurück-Link */}
          <div className="mt-6 pt-6 border-t border-white/[0.06] text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-gold transition-colors"
            >
              <ArrowLeft size={16} />
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}